"use strict";
// Card 1731: a captured provisional or final consumes the prior advances of its ledger. The
// application is derived by the system from the open targets, never typed by the operator, and
// the stored amount is the net (PR-11 FR-17). The backend derives with this rule inside the capture
// transaction and the capture dialog previews the same rule from the write-context read, so the
// two can never disagree on what a paper will consume.
Object.defineProperty(exports, "__esModule", { value: true });
exports.eligibleCaptureTargets = eligibleCaptureTargets;
exports.deriveCaptureApplications = deriveCaptureApplications;
exports.appliedTotalOf = appliedTotalOf;
exports.appliedToProvisionalsOf = appliedToProvisionalsOf;
exports.evaluateCaptureChecks = evaluateCaptureChecks;
const money_1 = require("./money");
// An advance is order-level money and the next paper on the side spends it. A prior provisional is
// spent only by a final, and only one that bills the same BL; a final that names no BL settles the
// whole side, so it spends every prior provisional.
function eligibleCaptureTargets(targets, paper) {
    return targets.filter((target) => {
        if ((0, money_1.isZeroMoney)(target.remaining) || (0, money_1.isNegativeMoney)(target.remaining))
            return false;
        if (target.kind === 'advance')
            return true;
        if (paper.kind !== 'final')
            return false;
        return paper.anchorBlRef === null || target.anchorBlRef === paper.anchorBlRef;
    });
}
function spendOver(budget, targets) {
    const applications = [];
    let left = budget;
    for (const target of targets) {
        if ((0, money_1.isZeroMoney)(left) || (0, money_1.isNegativeMoney)(left))
            break;
        const amount = (0, money_1.compareMoney)(target.remaining, left) > 0 ? left : target.remaining;
        applications.push({ documentId: target.documentId, amount });
        left = (0, money_1.subtractMoney)(left, amount);
    }
    return applications;
}
// Oldest first, in the order the targets arrive, each up to what it has left. With no printed
// deduction the paper spends up to its gross (the derived branch). With a printed advance deduction
// alone the paper spends exactly that figure over every eligible target, or the total remaining
// when the figure is larger (the printed branch). Card 1680: with a printed provisional deduction
// the paper states two lines, and each line spends its own kind: the advance figure (or nothing,
// when the paper prints no such line) spends the advances, the provisional figure spends the same-BL
// provisionals, and neither overflows into the other kind. The caller orders the list (advances
// before provisionals, each by creation time).
function deriveCaptureApplications(gross, targets, paper, printed = null) {
    const printedAdvanceDeduction = printed?.advanceDeduction ?? null;
    const printedProvisionalDeduction = printed?.provisionalDeduction ?? null;
    const eligible = eligibleCaptureTargets(targets, paper);
    if (printedProvisionalDeduction === null) {
        return spendOver(printedAdvanceDeduction ?? gross, eligible);
    }
    return [
        ...spendOver(printedAdvanceDeduction ?? money_1.ZERO_MONEY, eligible.filter((target) => target.kind === 'advance')),
        ...spendOver(printedProvisionalDeduction, eligible.filter((target) => target.kind === 'provisional')),
    ];
}
function appliedTotalOf(applications) {
    if (applications.length === 0)
        return money_1.ZERO_MONEY;
    return (0, money_1.sumMoney)(applications.map((application) => application.amount));
}
// The part of an application set that consumed prior provisionals, for the provisional line check.
function appliedToProvisionalsOf(applications, targets) {
    const provisionalIds = new Set(targets.filter((target) => target.kind === 'provisional').map((target) => target.documentId));
    return appliedTotalOf(applications.filter((application) => provisionalIds.has(application.documentId)));
}
function outcomeOf(expected, printed) {
    if (printed === null)
        return 'not_stated';
    return (0, money_1.compareMoney)(expected, printed) === 0 ? 'match' : 'mismatch';
}
// With one printed line the advance figure is the paper's whole deduction and reads against the
// whole applied total; with two printed lines each figure reads against its own kind.
function evaluateCaptureChecks(input) {
    const net = (0, money_1.subtractMoney)(input.gross, input.applied);
    const billedAfter = (0, money_1.sumMoney)([input.billedBefore ?? money_1.ZERO_MONEY, net]);
    const appliedToAdvances = input.printedProvisionalDeduction === null
        ? input.applied
        : (0, money_1.subtractMoney)(input.applied, input.appliedToProvisionals);
    return {
        net,
        appliedToAdvances,
        appliedToProvisionals: input.appliedToProvisionals,
        advanceDeduction: outcomeOf(appliedToAdvances, input.printedAdvanceDeduction),
        provisionalDeduction: outcomeOf(input.appliedToProvisionals, input.printedProvisionalDeduction),
        amountDue: outcomeOf(net, input.printedAmountDue),
        billedAfter,
        overExpected: input.expectedTotal === null ? null : (0, money_1.compareMoney)(billedAfter, input.expectedTotal) > 0,
    };
}
//# sourceMappingURL=capture-application.js.map